// ─── Public Status Page — header ─────────────────────────────────────
//
// Brand and exactly ONE action (Tilmeld). Nothing else belongs here: a status
// page's header is read by someone who is already having a bad day, and every
// extra control is one more thing between them and the answer.
//
// THERE IS NO AUDIENCE SWITCHER, and its absence is deliberate. An audience page
// is reached by the LINK its customers were given — `/<slug>` — not by browsing
// to it. Offering the full list in the chrome did the opposite of what a scoped
// page is for: it invited every visitor to page through other customers' service
// catalogues, and it let someone on their own correct page wander onto the
// combined one and conclude that a system they do not buy is down. The audience
// still reaches its page (the route is unchanged, and `summary.audience.name`
// names it under the logo); it is simply not advertised.
//
// THE BAND. The header is a solid deep-teal bar (.sp-header in status.css), not
// the warm ground the rest of the page sits on. That is not decoration: the C&B
// logomark is a light beige wordmark — it is drawn to sit on cb.dk's own solid
// dark header and is very nearly invisible on #efece9. cb.dk solves this with a
// solid header (`--solidHeaderBackgroundColor`), so this page does the same, and
// the band bookends the page against the teal footer that was already there.
//
// THE MARK IS THE BRAND, THE TEXT IS THE PAGE. The logomark already reads
// "C&B", so setting the word "C&B" beside it would say the same thing twice.
// The adjacent text is the PAGE's name instead ("Driftsstatus"), with the
// active audience as a quiet second line. Which is why the <img> carries a real
// alt: nothing else here names the company, and on a phone (where the text is
// hidden) the mark is all that is left.
//
// The brand is a real <a href> so middle-click and Ctrl-click open a tab; only a
// plain left click is intercepted into a client-side route change. It goes to
// THIS page's overview, not to cb.dk: from an incident deep link the mark is the
// way back, and hijacking it to leave the site mid-outage would be hostile. The
// route to the company site lives in the footer (see StatusFooter).

import type { MouseEvent } from 'react';
import { da } from '../i18n/da';
import { isPlainLeftClick } from '../router';
import type { StatusHeaderProps } from '../types';
import { SIcon } from '../ui/SIcon';

export function StatusHeader({
  audienceName,
  subscribeEnabled,
  onSubscribe,
  onHome,
  homeHref,
}: StatusHeaderProps) {
  const handleBrandClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!isPlainLeftClick(event)) return;
    event.preventDefault();
    onHome();
  };

  return (
    <header className="sp-header">
      <div className="sp-header-inner sp-container">
        <a className="sp-brand" href={homeHref} onClick={handleBrandClick}>
          {/* NOT decorative — see the note at the top of this file. The mark is
              the "C&B" wordmark and the only thing here that names the company,
              so it takes the brand as its alt; the text beside it names the
              PAGE. width/height are the file's real intrinsic pixels, so the
              browser reserves the right box from the aspect ratio before the
              image lands (status.css sets the rendered height). */}
          {/* Intrinsic size is the asset's REAL 750x316 — the browser reserves the
              box from these before the image loads, so a wrong pair costs layout
              shift on the one page people open when they are already anxious. */}
          <img className="sp-brand-mark" src="/cb-logomark.png" alt={da.brand} width={750} height={316} />
          {/* Both labels are dropped on a phone: the mark carries the identity
              and the banner one line below carries the verdict. */}
          <span className="sp-brand-text u-compact-hide">{da.pageTitle}</span>
          {/* The audience is stated, not chosen — this is the only thing that
              tells a customer on /<slug> that they are on their own page. */}
          {audienceName && <span className="sp-brand-sub u-compact-hide">{audienceName}</span>}
        </a>

        <div className="sp-header-actions">
          {subscribeEnabled && (
            <button type="button" className="sp-btn sp-btn--primary" onClick={onSubscribe}>
              <SIcon name="mail" size={16} />
              {da.subscribe}
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
